import { useCallback } from 'react';
import { LATEST_MODEL_VERSION, MODEL_VERSION, MODEL_VERSION_SETTINGS_MAP } from '@/app/constants';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { FormParams } from '@/app/stores/slices/task-form';
import type { ModelVersion, MultiFrameTransition } from '@/app/types';
import { adjustTransitionIndices, createDefaultTransition, validateAndFixDuration } from '@/app/utils';

/**
 * 智能多帧转场同步逻辑 Hook
 *
 * 职责：
 * 1. 转场同步：根据图片数量变化自动同步转场数据
 * 2. 数据协调：转场与表单字段（textPrompt、duration）的双向同步
 *
 */
export const useTransitionSync = () => {
  const { updateFormParams, setFormParams } = useStore((state) => ({
    updateFormParams: state.updateFormParams,
    setFormParams: state.setFormParams,
  }));
  const store = useStoreApi();

  /**
   * 获取表单的 settings 和 textPrompt
   */
  const getFormSettingsAndPrompt = useCallback(() => {
    const formParams = store.getState().img2videoFormParams;
    const modelVersion = formParams.modelVersion || MODEL_VERSION.Q2;
    const settingsKey = MODEL_VERSION_SETTINGS_MAP[(modelVersion || LATEST_MODEL_VERSION) as ModelVersion];
    const currentSettings = formParams[settingsKey] as { duration?: number } | undefined;
    const textPrompt = formParams.textPrompt;
    return { settingsKey, currentSettings, textPrompt, modelVersion };
  }, [store]);

  /**
   * 将单个转场信息同步到表单的 textPrompt 和 duration
   * 当转场数量减少到 1 个时，将其信息同步到外部表单字段，然后删除 multiFrameTransitions
   */
  const syncSingleTransitionToForm = useCallback(
    (
      currentTransitions: MultiFrameTransition[],
      settingsKey: keyof FormParams,
      currentSettings?: { duration?: number },
    ) => {
      if (currentTransitions.length > 0 && currentTransitions[0]) {
        const firstTransition = currentTransitions[0];
        // [0] 号 prompt 填入 textPrompt
        if (firstTransition.prompt) {
          setFormParams('img2video', {
            textPrompt: firstTransition.prompt,
            multiFrameTransitions: undefined,
          });
        } else {
          setFormParams('img2video', {
            multiFrameTransitions: undefined,
          });
        }
        // duration 填入 settings.duration
        if (firstTransition.duration) {
          updateFormParams('img2video', settingsKey, {
            ...currentSettings,
            duration: firstTransition.duration,
          });
        }
      } else {
        setFormParams('img2video', { multiFrameTransitions: undefined });
      }
    },
    [setFormParams, updateFormParams],
  );

  /**
   * 处理从 <=2 变成 >2 的情况：创建 multiFrameTransitions
   * 把 textPrompt 和 settingsDuration 同步到第一个转场
   */
  const createTransitions = useCallback(
    (transitionCount: number, textPrompt?: string, settingsDuration?: number) => {
      const formParams = store.getState().img2videoFormParams;
      const modelVersion = formParams.modelVersion || MODEL_VERSION.Q2;

      // 验证并修正 duration 值，确保在合法范围内
      const validatedDuration = validateAndFixDuration(settingsDuration, modelVersion);

      const newTransitions: MultiFrameTransition[] = [];
      // [0]号数据的 prompt 使用 textPrompt，duration 使用验证后的 settings.duration
      newTransitions.push(createDefaultTransition(0, 1, textPrompt, validatedDuration));

      // 创建剩余的转场项
      for (let i = 1; i < transitionCount; i++) {
        newTransitions.push(createDefaultTransition(i, i + 1));
      }

      setFormParams('img2video', { multiFrameTransitions: newTransitions });
    },
    [setFormParams, store],
  );

  /**
   * 调整转场数量：根据图片数生成合理数量的转场项（N 张图 => N-1 个转场）
   */
  const adjustTransitionsCount = useCallback(
    (
      transitionCount: number,
      currentTransitions: MultiFrameTransition[],
      textPrompt?: string,
      settingsDuration?: number,
    ) => {
      const formParams = store.getState().img2videoFormParams;
      const modelVersion = formParams.modelVersion || MODEL_VERSION.Q2;

      // 验证并修正 duration 值，确保在合法范围内
      const validatedDuration = validateAndFixDuration(settingsDuration, modelVersion);

      const hadTransitions = currentTransitions.length > 0;
      const newTransitions: MultiFrameTransition[] = [];
      for (let i = 0; i < transitionCount; i++) {
        if (currentTransitions[i]) {
          newTransitions.push(currentTransitions[i]);
        } else if (i === 0 && !hadTransitions && textPrompt) {
          // 第一个转场使用验证后的 duration
          newTransitions.push(createDefaultTransition(0, 1, textPrompt, validatedDuration));
        } else {
          newTransitions.push(createDefaultTransition(i, i + 1));
        }
      }
      setFormParams('img2video', { multiFrameTransitions: newTransitions });
    },
    [setFormParams, store],
  );

  /**
   * 处理删除图片时的转场调整
   */
  const handleRemoveImageTransition = useCallback(
    (currentTransitions: MultiFrameTransition[], removedIndex: number, onAfterSync?: () => void) => {
      if (!currentTransitions || currentTransitions.length === 0) {
        return;
      }

      const transitions = [...currentTransitions];

      // 根据删除的索引，删除对应的转场
      if (removedIndex === 0) {
        // 删除第一张图：删除 frame 0 到 frame 1 的转场（multiFrameTransitions[0]）
        transitions.shift();
      } else {
        // 删除非第一张图：删除"指向被删帧"的那一项（数组位置为 removedIndex - 1）
        if (removedIndex - 1 >= 0 && removedIndex - 1 < transitions.length) {
          transitions.splice(removedIndex - 1, 1);
        }
      }

      // 统一更新剩余项的 from / to
      const adjusted = adjustTransitionIndices(transitions, removedIndex);

      // 如果删除后只剩下一个转场，将其同步到 textPrompt 和 duration
      if (adjusted.length === 1) {
        const { settingsKey, currentSettings } = getFormSettingsAndPrompt();
        syncSingleTransitionToForm(adjusted, settingsKey, currentSettings);
        onAfterSync?.();
        return;
      }

      setFormParams('img2video', {
        multiFrameTransitions: adjusted.length > 0 ? adjusted : undefined,
      });

      onAfterSync?.();
    },
    [setFormParams, getFormSettingsAndPrompt, syncSingleTransitionToForm],
  );

  /**
   * 根据有效图片数量，同步 multiFrameTransitions
   *
   * @param newValidImageCount 新的有效图片数量
   * @param removedIndex 可选，被删除的图片索引（当删除图片时传入）
   * @param onAfterSync 可选，同步完成后的回调（用于处理删除图片后的额外逻辑）
   */
  const syncTransitionsAfterImageCountChange = useCallback(
    (newValidImageCount: number, removedIndex?: number, onAfterSync?: () => void) => {
      const formParams = store.getState().img2videoFormParams;
      const currentTransitions = formParams.multiFrameTransitions || [];
      const prevValidImageCount = currentTransitions.length + 1;
      const transitionCount = Math.max(0, newValidImageCount - 1);

      const { settingsKey, currentSettings, textPrompt } = getFormSettingsAndPrompt();

      if (removedIndex !== undefined) {
        handleRemoveImageTransition(currentTransitions, removedIndex, onAfterSync);
        return;
      }

      if (newValidImageCount <= 2) {
        if (prevValidImageCount > 2) {
          syncSingleTransitionToForm(currentTransitions, settingsKey, currentSettings);
          return;
        }

        // 直接删除 multiFrameTransitions
        setFormParams('img2video', { multiFrameTransitions: undefined });
        return;
      }

      // 从 <=2 变成 >2 时，创建 multiFrameTransitions
      if (prevValidImageCount <= 2) {
        createTransitions(transitionCount, textPrompt, currentSettings?.duration);
        return;
      }

      // 其他情况：根据图片数生成合理数量的转场项（N 张图 => N-1 个转场）
      if (currentTransitions.length !== transitionCount) {
        adjustTransitionsCount(transitionCount, currentTransitions, textPrompt, currentSettings?.duration);
      }
    },
    [
      store,
      getFormSettingsAndPrompt,
      handleRemoveImageTransition,
      syncSingleTransitionToForm,
      createTransitions,
      adjustTransitionsCount,
      setFormParams,
    ],
  );

  return {
    syncTransitionsAfterImageCountChange,
    handleRemoveImageTransition,
  };
};
